import {
  BreakpointConfigType,
  Breakpoints,
  DesignTokenSizeEnum,
  GallerySlide,
  ThemeName,
  ThemeProvider,
  styled,
} from '@volkswagen-onehub/components-core';
import { ReactElement, useRef, useState } from 'react';
import { CarouselNavigation } from '@tribal-worldwide-london/ui-component-library-vwg';
import {
  performVWBasicLayerClose,
  performVWBasicLayerLoad,
  performVWBasicLinkClick,
} from '@/analytics/events';
import { SectionNames } from '@/analytics/sectionNames';
import { useTribalContext } from '@/context/tribal';
import { useFeatures } from '@/hooks/useFeatures';
import useResize from '@/hooks/useResize';
import useResizeResponsive from '@/hooks/useResizeResponsive';
import {
  getBulletLabels,
  getCurrentSlidesPerPage,
  getNumberOfBullets,
} from '@/modules/components/Carousel/Carousel';
import { getActiveConfiguration } from '@/modules/utils/storeUtils';
import useGlobalStore from '@/stores/globalStore';
import {
  Box,
  CTAModal,
  CardFeaturedItem,
  Carousel,
  LimitContent,
} from '@/modules/components';
import useFeaturedImages from '@/modules/components/CardFeaturedItem/useFeaturedImages';

interface FeaturesCarouselProps {
  featuresData: any[];
  id: string;
  ariaLabel: string;
  slidesPerPage: number | BreakpointConfigType<number>;
  theme?: ThemeName;
  hideNavigationDesktop?: boolean;
  activeSlideIndex?: number;
  footerStacked?: boolean;
  sectionName?: SectionNames;
  decoratorImgIcon?: ReactElement;
}

const FeaturesCarouselWrapper = styled.div(
  ({ theme }) => `
  color: ${theme.colors.content.primary};
  [aria-roledescription='slide'] > div {
    height: 100%;
  }

  section > div > div > div:nth-child(1) {
    background-color: transparent;
  }

  *:focus {
    box-shadow: none;
  }
    .short-description {
      font-weight: 400;
      font-size: 16px;
      color: #000e26;
      line-height: 24px;
    }
`
);

function FeaturesCarousel(props: FeaturesCarouselProps) {
  const {
    featuresData,
    theme = 'main',
    hideNavigationDesktop,
    id,
    ariaLabel,
    footerStacked,
    sectionName,
    decoratorImgIcon,
    ...restProps
  } = props;
  const { contentManager, analyticsBuilder, analyticsManager } =
    useTribalContext();
  const { app } = useGlobalStore();
  const activeConfiguration = getActiveConfiguration(app);
  const [readMores, setReadMores] = useState<number[]>([]);
  const readMeRef = useRef<number[]>([]);
  const [currentSlide, setCurrentSlide] = useState(1);

  const readMoreLabel = contentManager.get('ctas-read-more');

  const addReadMore = (indices: number[]) => {
    setReadMores(indices);
  };

  const isBetweenBreakpoints = useResizeResponsive(
    ({ innerWidth }) =>
      innerWidth >= Breakpoints.b960 && innerWidth < Breakpoints.b1280
  );

  const resizeDimensions = useResize();

  const { getSpecItemLabel } = useFeatures();
  const getShortDescription = (feature: any) => {
    if (feature.shortDescription && feature.availability === 'optional-extra') {
     return (
      <div className="short-description">
        {feature.shortDescription}
      </div>
    );
    }
    return '';
  };

  const images = useFeaturedImages(
    featuresData.map(({ imageOptions }) => imageOptions)
  );

  const analyticsCommonFields = {
    sectionName,
    configuration: activeConfiguration,
    retailers: app.retailers,
    analyticsManager,
    analyticsBuilder,
  };

  const analyticsLinkFn = (label: string) => {
    performVWBasicLinkClick({
      label,
      ...analyticsCommonFields,
    });
  };

  const currentSlidesPerPage = getCurrentSlidesPerPage(
    restProps.slidesPerPage,
    resizeDimensions.innerWidth
  );

  const numberOfBullets = getNumberOfBullets(
    +currentSlidesPerPage,
    featuresData
  );
  const bulletLabels = getBulletLabels(numberOfBullets, sectionName);

  return (
    <FeaturesCarouselWrapper data-testid="features-carousel">
      <ThemeProvider variant="v26" theme={theme}>
        <Carousel
          {...restProps}
          id={id}
          aria-label={ariaLabel}
          gutter={DesignTokenSizeEnum.static350}
          bottomElements={
            <CarouselNavigation
              aria-label={`Navigation for ${ariaLabel}`}
              bulletLabels={bulletLabels}
              hideNavigationDesktop={hideNavigationDesktop}
            />
          }
          onSlideChange={(index: number) => setCurrentSlide(index)}
        >
          {featuresData.map((feature, i: number) => (
            <GallerySlide aria-labelledby={ariaLabel} key={feature.title}>
              <Box pt={DesignTokenSizeEnum.static400} height="100%">
                <ThemeProvider theme="main">
                  <CardFeaturedItem
                    body={
                      <LimitContent
                        maxLines={2}
                        onMaxLinesReached={() => {
                          addReadMore([...readMeRef.current, i]);
                        }}
                      >
                        {feature.title}
                      </LimitContent>
                    }
                    decoratorImgContent={<img src={images[i]} />}
                    height="100%"
                    header={feature.header}
                    maskSize={isBetweenBreakpoints ? 'medium' : 'large'}
                    footerStacked={footerStacked}
                    decoratorImgIcon={decoratorImgIcon}
                    links={
                      <>
                        {(feature.title !== feature.description ||
                          readMores.includes(i)) && (
                          <CTAModal
                            modalProps={feature}
                            tag="button"
                            onClick={() => analyticsLinkFn(readMoreLabel)}
                            openCallback={() =>
                              performVWBasicLayerLoad(analyticsCommonFields)
                            }
                            closeCallback={() =>
                              performVWBasicLayerClose(analyticsCommonFields)
                            }
                          >
                            {readMoreLabel}
                          </CTAModal>
                        )}
                        {feature.secondaryLink && (
                          <CTAModal
                            modalProps={{
                              title: feature.title,
                              description:
                                feature.title !== feature.description
                                  ? feature.description
                                  : null,
                              body: feature.secondaryLink.modalContent,
                            }}
                            tag="button"
                            onClick={() =>
                              analyticsLinkFn(feature.secondaryLink.label)
                            }
                            openCallback={() =>
                              performVWBasicLayerLoad(analyticsCommonFields)
                            }
                            closeCallback={() =>
                              performVWBasicLayerClose(analyticsCommonFields)
                            }
                          >
                            {feature.secondaryLink.label}
                          </CTAModal>
                        )}
                      </>
                    }
                    onSelect={() => {}}
                    price={
                      <>
                        {getSpecItemLabel(feature.availability, feature.price)}
                        {feature.excludedFromFinance ? '*' : ''}
                        <br />
                        {getShortDescription(feature)}
                      </>
                    }
                  />
                </ThemeProvider>
              </Box>
            </GallerySlide>
          ))}
        </Carousel>
      </ThemeProvider>
    </FeaturesCarouselWrapper>
  );
}

export default FeaturesCarousel;
